/* Bottom tab bar — the rail's mobile counterpart.

   Same contract as g-rail: it renders the defs it is given and emits `navigate`.
   It never imports a workspace or the router.

   Differences from the rail, all forced by the platform rather than chosen:

   - **Zones are ignored.** `WorkspaceDef.zone` sorts the rail into primary and
     utility groups; a tab bar has no second group, and Settings has to be a peer
     because removing the header removed its only other entry point (ADR 0007
     decision 4).
   - **No labels at all.** Icon-only since ADR 0009 decision 1, which reverses
     0007's "labels always show". The accessible name therefore comes from
     `aria-label` rather than visible text — that attribute is load-bearing, not
     decoration, and removing it makes the whole bar anonymous to a screen
     reader.
   - **Badges live here.** The header's queue indicator has nowhere else to go on
     mobile, and a numeric badge on a tab is what the platform uses for exactly
     this (ADR 0007 decision 3).
   - **One tab may render an avatar instead of an icon**, declared by the
     workspace via `WorkspaceDef.identity` so this file never learns which
     workspace that is (ADR 0009 decision 2).
   - **One entry is an action, not a destination** — search (ADR 0019). It emits
     `search` rather than `navigate`, never takes `aria-current`, and the shell
     says when it is unusable. It stays inside the one `<nav>`: it sits in the
     middle of the order, and since the nav is `display: contents` the DOM order
     IS the visual order, so hoisting the button out would mean either two nav
     landmarks or a broken order. A button inside a nav is valid and announces as
     a button, which is the accurate thing here.

   Not built on g-button: a tab stacks its icon over its label and divides the
   width evenly, where g-button is a horizontal row that hugs its content. The
   two have almost no styling in common. */

import { LitElement, html, css, nothing } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import type { Me, TabAction, TabItem, WorkspaceDef } from '../../core/types.js';
import { isTabAction } from '../../core/types.js';
import '../../component/g-avatar/g-avatar.js';
import '../../component/g-icon/g-icon.js';

@customElement('g-tabbar')
export class GTabbar extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: flex;
      flex: none;
      border-top: 1px solid var(--border);
      background: var(--surface);
      /* The bar is the last thing above the home indicator, so it owns the
         bottom inset. Padding rather than height so the row of tabs stays put
         and the gesture area below it is just background. */
      padding-bottom: var(--safe-b);
      padding-left: var(--safe-l);
      padding-right: var(--safe-r);
    }
    button {
      flex: 1;
      /* Five tabs share the width, so each is ~72px on the narrowest phone —
         comfortably past --touch-min without needing the hit-area trick. Height
         is --tabbar-h, which is --touch-min exactly; see the token. */
      min-width: 0;
      height: var(--tabbar-h);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 2px;
      border: 0;
      background: none;
      color: var(--text-dim);
      font: inherit;
      font-family: var(--font-ui);
      cursor: pointer;
      transition: opacity 120ms ease;
    }
    /* The tap flash this replaces is killed globally now (design/base.ts). A tab
       has no background of its own to tint and its glyph fills the button, so
       the press reads as a dip on the glyph rather than a box behind it. */
    button:active {
      opacity: 0.5;
    }
    button[aria-current='page'] {
      color: var(--accent);
    }
    /* Dimmed and inert rather than removed: dropping the entry would reflow the
       whole bar from five tabs to four on entering Settings, moving every tab
       under the thumb that is already reaching for one. The header makes the same
       trade with its own field for the same reason. */
    button[disabled] {
      opacity: 0.35;
      cursor: default;
    }
    button[disabled]:active {
      opacity: 0.35;
    }
    /* The icon is wrapped so the badge can sit against the glyph rather than the
       whole tab — against the tab it drifts miles from what it is counting.
       26px rather than the rail's 22: with the label gone the glyph is the only
       thing identifying the tab, and it has the vertical room. */
    .glyph {
      position: relative;
      display: flex;
      --icon-size: 26px;
      --avatar-size: 26px;
    }
    /* An avatar can't take the accent colour the way a stroked icon does — it's
       a photograph. A ring is the platform's answer, and outline rather than
       border so the circle itself doesn't resize between states. */
    button[aria-current='page'] g-avatar {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
    .badge {
      position: absolute;
      top: -4px;
      left: 60%;
      min-width: 15px;
      padding: 0 3px;
      border-radius: var(--radius-full);
      background: var(--accent);
      color: var(--accent-ink);
      font-family: var(--font-mono);
      font-size: 0.6rem;
      line-height: 15px;
      text-align: center;
    }
  `];

  @property({ attribute: false }) items: TabItem[] = [];
  @property() active = '';
  /**
   * Actions the current workspace cannot service, by action id.
   *
   * Computed by the shell from the active `WorkspaceDef` — this file must not
   * learn that a workspace called "settings" has no search.
   */
  @property({ attribute: false }) disabledActions: string[] = [];
  /** Workspace id → count. Absent or zero renders nothing (§23: no idle "0"). */
  @property({ attribute: false }) badges: Record<string, number> = {};
  /**
   * Whose face to show on the `identity` tab.
   *
   * Under `?as=` impersonation this is the impersonated user, because that is
   * what /api/me returns — which makes the tab a standing, unmissable signal of
   * who you are acting as. That is a feature, not a leak of the mechanism.
   */
  @property({ attribute: false }) me: Me | null = null;

  private go(id: string) {
    this.dispatchEvent(new CustomEvent('navigate', { detail: id, bubbles: true, composed: true }));
  }

  /** Composed, because the shell listens from outside this shadow root. */
  private act(id: string) {
    this.dispatchEvent(new CustomEvent('search', { detail: id, bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <nav aria-label="Workspaces" style="display:contents">
        ${this.items.map((w) => (isTabAction(w) ? this.renderAction(w) : this.renderTab(w)))}
      </nav>
    `;
  }

  private renderTab(w: WorkspaceDef) {
    const n = this.badges[w.id] ?? 0;
    return html`
      <button
        aria-label=${w.label}
        aria-current=${w.id === this.active ? 'page' : nothing}
        @click=${() => this.go(w.id)}
      >
        <span class="glyph">
          ${w.identity
            ? html`<g-avatar .me=${this.me}></g-avatar>`
            : html`<g-icon name=${w.icon}></g-icon>`}
          ${n > 0 ? html`<span class="badge" aria-label=${`${n} in progress`}>${n}</span>` : nothing}
        </span>
      </button>
    `;
  }

  /* No aria-current and no badge: it is not a place to be at, and it counts
     nothing. Stateless even while the field it opens is up — the band's own query
     pill is what says a search is active. */
  private renderAction(a: TabAction) {
    const off = this.disabledActions.includes(a.id);
    return html`
      <button
        aria-label=${a.label}
        ?disabled=${off}
        @click=${() => this.act(a.id)}
      >
        <span class="glyph"><g-icon name=${a.icon}></g-icon></span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-tabbar': GTabbar;
  }
}
